import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ClockIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { useWalletStore } from '../store/walletStore';
import {
  getPresaleInfo,
  getTokenInfo,
  contributePresaleOps,
  claimPresaleOps,
  refundPresaleOps,
  cancelPresaleOps,
  finalizePresaleOps,
  displayToRaw,
} from '../api/vexforge';
import { signAndSend } from '../lib/submit';

export default function PresaleDetail() {
  const { mint } = useParams();
  const { address, isConnected } = useWalletStore();
  const [presale, setPresale] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [contributing, setContributing] = useState(false);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    async function fetchData() {
      try {
        const [ps, tk] = await Promise.allSettled([
          getPresaleInfo(mint),
          getTokenInfo(mint),
        ]);
        if (ps.status === 'fulfilled') setPresale(ps.value);
        if (tk.status === 'fulfilled') setToken(tk.value);
      } catch (err) {
        console.error('Failed to fetch presale:', err);
      } finally {
        setLoading(false);
      }
    }
    if (mint) fetchData();
  }, [mint]);

  const isCreator = isConnected && presale?.creator &&
    address?.toLowerCase() === presale.creator.toLowerCase();

  const handleContribute = async () => {
    if (!amount) return;
    setContributing(true);
    try {
      // Entered in VXS; signed as raw nanoVXS (9 dp).
      const ops = contributePresaleOps(mint, displayToRaw(amount, 9, 'Contribution'));
      const txHash = await signAndSend(ops, `Contribute ${amount} VXS to ${token?.symbol || 'token'} presale`);
      if (!txHash) return;
      toast.success(`Contribution submitted (tx ${String(txHash).slice(0, 14)}…) — refresh once confirmed`);
      setAmount('');
    } catch (err) {
      toast.error(err.message || 'Contribution failed');
    } finally {
      setContributing(false);
    }
  };

  const handleAction = async (buildOps, label) => {
    try {
      const txHash = await signAndSend(buildOps(mint), `${label} ${token?.symbol || 'token'} presale`);
      if (!txHash) return;
      toast.success(`${label} submitted (tx ${String(txHash).slice(0, 14)}…) — refresh once confirmed`);
    } catch (err) {
      toast.error(err.message || `${label} failed`);
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-gray-400">Loading presale...</div>;
  }

  if (!presale) {
    return (
      <div className="text-center py-16">
        <h1 className="text-2xl font-bold text-white mb-2">Presale Not Found</h1>
        <Link to="/presales" className="text-vextaris-blue hover:text-white">Back to Presales</Link>
      </div>
    );
  }

  const progress = presale.hard_cap ? Math.min((presale.total_raised / presale.hard_cap) * 100, 100) : 0;
  const softCapProgress = presale.hard_cap && presale.soft_cap ? (presale.soft_cap / presale.hard_cap) * 100 : 50;

  const statusColors = {
    Active: 'bg-vextaris-lime/20 text-vextaris-lime border-vextaris-lime/30',
    Pending: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    Finalized: 'bg-green-500/20 text-green-400 border-green-500/30',
    Cancelled: 'bg-red-500/20 text-red-400 border-red-500/30',
  };
  const statusClass = statusColors[presale.status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-white">{token?.name || 'Token'} Presale</h1>
            <p className="text-gray-400 font-mono text-sm mt-1">{mint}</p>
          </div>
          <span className={`px-4 py-2 rounded-xl border text-sm font-bold ${statusClass}`}>
            {presale.status}
          </span>
        </div>

        {/* Progress Bar */}
        <div className="relative mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-gray-400">Raised</span>
            <span className="text-white font-semibold">{presale.total_raised || 0} / {presale.hard_cap || '--'} VXS</span>
          </div>
          <div className="relative w-full h-4 bg-vextaris-darker rounded-full overflow-hidden">
            <div className="h-full bg-gradient-vextaris rounded-full transition-all" style={{ width: `${progress}%` }} />
            {/* Soft cap marker */}
            <div
              className="absolute top-0 h-full w-0.5 bg-yellow-400"
              style={{ left: `${softCapProgress}%` }}
              title={`Soft Cap: ${presale.soft_cap} VXS`}
            />
          </div>
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>0</span>
            <span className="text-yellow-400">Soft: {presale.soft_cap || '--'}</span>
            <span>Hard: {presale.hard_cap || '--'}</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <CurrencyDollarIcon className="w-5 h-5 text-vextaris-blue mx-auto mb-1" />
            <p className="text-xs text-gray-400">Price</p>
            <p className="text-sm font-semibold text-white">{presale.price_per_token || '--'} VXS</p>
          </div>
          <div className="text-center">
            <ShieldCheckIcon className="w-5 h-5 text-vextaris-blue mx-auto mb-1" />
            <p className="text-xs text-gray-400">LP Lock</p>
            <p className="text-sm font-semibold text-white">{presale.lp_percent || '--'}%</p>
          </div>
          <div className="text-center">
            <UserGroupIcon className="w-5 h-5 text-vextaris-blue mx-auto mb-1" />
            <p className="text-xs text-gray-400">Contributors</p>
            <p className="text-sm font-semibold text-white">{presale.contributor_count || 0}</p>
          </div>
          <div className="text-center">
            <ClockIcon className="w-5 h-5 text-vextaris-blue mx-auto mb-1" />
            <p className="text-xs text-gray-400">Ends</p>
            <p className="text-sm font-semibold text-white">
              {presale.end_time ? new Date(presale.end_time * 1000).toLocaleDateString() : '--'}
            </p>
          </div>
        </div>
      </div>

      {/* Contribution Limits */}
      <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6">
        <h2 className="text-lg font-semibold text-white mb-3">Contribution Limits</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Min:</span>
            <span className="text-white ml-2">{presale.min_contribution || '--'} VXS</span>
          </div>
          <div>
            <span className="text-gray-400">Max:</span>
            <span className="text-white ml-2">{presale.max_contribution || '--'} VXS</span>
          </div>
          <div>
            <span className="text-gray-400">Token Allocation:</span>
            <span className="text-white ml-2">{presale.token_allocation || '--'}</span>
          </div>
          <div>
            <span className="text-gray-400">LP Lock Duration:</span>
            <span className="text-white ml-2">{presale.lp_lock_duration ? `${Math.floor(presale.lp_lock_duration / 86400)} days` : '--'}</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      {isConnected && (
        <div className="bg-vextaris-dark/50 backdrop-blur-xl rounded-2xl border border-vextaris-purple/20 p-6 space-y-4">
          {presale.status === 'Active' && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-3">Contribute</h2>
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="Amount in VXS"
                  className="flex-1 px-4 py-2 bg-vextaris-darker border border-vextaris-purple/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-vextaris-purple"
                />
                <button
                  onClick={handleContribute}
                  disabled={contributing || !amount}
                  className="px-6 py-2 bg-gradient-vextaris text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                >
                  {contributing ? 'Contributing...' : 'Contribute'}
                </button>
              </div>
            </div>
          )}

          {presale.status === 'Finalized' && (
            <button onClick={() => handleAction(claimPresaleOps, 'Claim')}
              className="w-full px-4 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700">
              Claim Tokens
            </button>
          )}

          {presale.status === 'Cancelled' && (
            <button onClick={() => handleAction(refundPresaleOps, 'Refund')}
              className="w-full px-4 py-3 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700">
              Claim Refund
            </button>
          )}

          {isCreator && (
            <div className="flex space-x-3 pt-2 border-t border-vextaris-purple/20">
              {(presale.status === 'Active' || presale.status === 'Pending') && (
                <button onClick={() => handleAction(cancelPresaleOps, 'Cancel')}
                  className="flex-1 px-4 py-2 bg-red-500/10 text-red-400 rounded-lg font-medium hover:bg-red-500/20">
                  Cancel Presale
                </button>
              )}
              {presale.status === 'Active' && (
                <button onClick={() => handleAction(finalizePresaleOps, 'Finalize')}
                  className="flex-1 px-4 py-2 bg-green-500/10 text-green-400 rounded-lg font-medium hover:bg-green-500/20">
                  Finalize Presale
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}